import React, { useMemo, useRef, useContext } from "react";
import { DataTableContext } from "../Tabs/Overview";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import { ViewMasterCampaignsContext } from "../index";
import ExportConfirmationModal from "../../Modals/ExportConfirmationModal";
import AppConfig from "../../../AppConfig";
import PushToPlatformCampaignModal from "../../Modals/PushToPlatformCampaignModal";
import { AddCampaignToPrisma, AddCampaignToDV360 } from "../../../Flux/Actions/MasterCampaignActions";
import Tooltip, { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import { multipleMasterCampaignsSelected } from "./actionValidations";

export default () => {
	// define a ref that will be used to open and close the modal
	const dpRef = useRef();

	// import various contexts
	const { checkboxSelections, dataProvider, setLoading, dataTableApi } = useContext(DataTableContext);
	const { enabledPlatformAPIs, platforms } = useContext(ViewMasterCampaignsContext);

	// gather all relevant info used to inform further memos.
	const selectedCampaignInfo = useMemo(() => {
		try {
			if (checkboxSelections && platforms && dataProvider) {
				const sac = checkboxSelections?.selectedActivationCampaigns || [];
				if (sac?.length == 1) {
					const { platform, masterCampaignId, activationCampaignId } = sac?.[0];
					const platformId = platforms?.find(({ PlatformName }) => PlatformName == platform)?.MasterPlatformID;

					const activationCampaign = dataProvider?.data
						?.find(({ MasterCampaignID }) => MasterCampaignID == masterCampaignId)
						?.ActivationCampaigns?.[platform]?.find(({ CampaignID }) => CampaignID == activationCampaignId);

					const advertisers = activationCampaign?.PlatformAdvertisers ? JSON.parse(activationCampaign?.PlatformAdvertisers) : [];

					return {
						platformId,
						activationCampaignId,
						status: activationCampaign?.ExternalSystemStatusID,
						advertiserId: advertisers?.find(({ MasterPlatformID }) => MasterPlatformID == platformId)?.PlatformAdvertiserID || null,
						isDraft: activationCampaign?.IsDraft === "True",
						budgetLocked: activationCampaign?.IsPrismaBudgetLocked === "True"
					};
				}
				return null;
			}
		} catch (e) {
			return null;
		}
	}, [checkboxSelections, platforms, dataProvider]);

	const shown = useMemo(() => {
		if (selectedCampaignInfo && enabledPlatformAPIs) {
			const { platformId } = selectedCampaignInfo;
			if (platformId == 1 || platformId == 2 || platformId == 5) {
				return enabledPlatformAPIs?.find(({ MasterPlatformID }) => MasterPlatformID == platformId)?.SyncStatus || false;
			}
		}
		return false;
	}, [enabledPlatformAPIs, selectedCampaignInfo]);

	const buttonLabel = useMemo(() => {
		if (selectedCampaignInfo) {
			const { platformId } = selectedCampaignInfo;
			if (platformId == 1) {
				return "Push GCM Campaign";
			}
			if (platformId == 2) {
				return "Push DV360 Campaign";
			}
			if (platformId == 5) {
				return "Push Prisma Campaign";
			}
		} else {
			return "";
		}
	}, [selectedCampaignInfo]);

	const disabled = useMemo(() => {
		if (selectedCampaignInfo?.isDraft) {
			return true;
		}
		// Allow GCM, prisma, dv360 platforms that are either Draft, Failed, or Revision Needed.
		// See PlatformStatusIcon.jsx for full status map
		if (shown && selectedCampaignInfo) {
			const { platformId, status } = selectedCampaignInfo;

			// prettier-ignore
			if (
                (
                    (platformId == 1 || platformId == 2 || platformId == 5) && 
                    (status == 1 || status == 2 || status == 6)
                ) || 
                (platformId == 5 && status == 3)
            ) {
				return false;
			}
		}
		return true;
	}, [shown, selectedCampaignInfo]);

	const multipleMasterCampaigns = useMemo(() => multipleMasterCampaignsSelected(checkboxSelections), [checkboxSelections]);

	const savePushToPlatformCampaign = async (masterCampaignID, advertiserID, platformID, financialSettings) => {
		setLoading(true);
		if (platformID == 5) {
			await AddCampaignToPrisma(masterCampaignID, advertiserID, financialSettings);
		} else if (platformID == 2) {
			await AddCampaignToDV360(masterCampaignID, advertiserID);
		}
		// setLoading(false);
		dataTableApi?.current?.rowSelectionManager?.tree?.selectNode?.(false);
		setTimeout(() => dataProvider?.update?.(), 1);
	};

	const Trigger = () => {
		if (shown) {
			return (
				<Tooltip title={buttonLabel} position={TOOLTIP_POSITION.BOTTOM}>
					<Button
						display={BUTTON_DISPLAY.ICON_BORDERED}
						size={BUTTON_SIZE.SMALL}
						icon="icon-notify"
						onClick={() => {
							if (selectedCampaignInfo) {
								const { activationCampaignId, platformId, advertiserId } = selectedCampaignInfo;
								dpRef?.current?.triggerOpenClose?.(advertiserId, activationCampaignId, platformId);
							}
						}}
						disabled={disabled || multipleMasterCampaigns}
						id="pushCampaignButton"
						data-tracking-id-alt="open-push-campaign-modal"
					>
						{buttonLabel}
					</Button>
				</Tooltip>
			);
		}
		return <></>;
	};

	const DisplayedComponent = () => {
		return (
			<PushToPlatformCampaignModal
				pushToPlatformCampaignModal={(ref) => {
					dpRef.current = ref;
				}}
				selectedCampaignInfo={selectedCampaignInfo}
				onSubmit={savePushToPlatformCampaign}
			/>
		);
	};

	// return a memoized object containing both of these components
	return useMemo(() => ({ Trigger, DisplayedComponent }), [shown, disabled, buttonLabel, selectedCampaignInfo]);
};
